import { useState, useRef } from 'react'
import QRCode from 'qrcode'
import { authApi } from '../../api/auth'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onBack: () => void
}

type Step = 'intro' | 'scan' | 'verify' | 'done'

export function TwoFactorSetup({ onBack }: Props) {
  const [step, setStep] = useState<Step>('intro')
  const [secret, setSecret] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { addToast } = useUiStore()

  const handleEnable = async () => {
    setLoading(true)
    try {
      const res = await authApi.enable2FA()
      setSecret(res.totp_secret)
      const dataUrl = await QRCode.toDataURL(res.qr_url, { width: 200, margin: 1 })
      setQrDataUrl(dataUrl)
      setStep('scan')
    } catch {
      addToast('error', 'Не удалось настроить двухфакторную аутентификацию')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (code.length !== 6) return
    setLoading(true)
    try {
      await authApi.verify2FA(code)
      setStep('done')
      addToast('success', 'Двухфакторная аутентификация включена')
    } catch {
      addToast('error', 'Неверный код — попробуйте снова')
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold">Двухфакторная аутентификация</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {step === 'intro' && (
          <div className="flex flex-col items-center gap-5 py-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-accent/15 flex items-center justify-center text-3xl">
              🔐
            </div>
            <div>
              <h3 className="text-txt-primary font-semibold text-lg mb-2">Защитите аккаунт</h3>
              <p className="text-txt-secondary text-sm leading-relaxed">
                Двухфакторная аутентификация добавляет дополнительный уровень защиты. Вам понадобится
                приложение-аутентификатор (Google Authenticator, Authy и др.) для генерации одноразовых кодов.
              </p>
            </div>
            <button
              onClick={handleEnable}
              disabled={loading}
              className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
            >
              {loading ? 'Настройка…' : 'Включить 2FA'}
            </button>
          </div>
        )}

        {step === 'scan' && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-txt-secondary text-sm text-center">
              Отсканируйте QR-код в приложении-аутентификаторе
            </p>
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt="QR-код 2FA"
                className="w-48 h-48 rounded-xl border border-divider bg-white p-2"
              />
            )}
            <div className="w-full bg-bg-input rounded-xl p-3">
              <p className="text-txt-meta text-xs mb-1">Или введите этот ключ вручную:</p>
              <p className="text-txt-primary font-mono text-sm break-all select-all">{secret}</p>
            </div>
            <button
              onClick={() => setStep('verify')}
              className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors"
            >
              Я отсканировал(а) код
            </button>
          </div>
        )}

        {step === 'verify' && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-txt-secondary text-sm text-center">
              Введите 6-значный код из приложения-аутентификатора для подтверждения
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              placeholder="000000"
              autoFocus
              className="w-40 text-center text-2xl font-mono tracking-widest bg-bg-input border border-divider rounded-xl px-4 py-3 text-txt-primary outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
            >
              {loading ? 'Проверка…' : 'Подтвердить и включить'}
            </button>
            <button onClick={() => setStep('scan')} className="text-txt-meta text-sm hover:text-txt-secondary">
              ← Назад к QR-коду
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center gap-5 py-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/15 flex items-center justify-center text-3xl">
              ✅
            </div>
            <div>
              <h3 className="text-txt-primary font-semibold text-lg mb-2">2FA включена!</h3>
              <p className="text-txt-secondary text-sm leading-relaxed">
                Ваш аккаунт теперь защищён. При следующем входе потребуется приложение-аутентификатор.
              </p>
            </div>
            <button
              onClick={onBack}
              className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors"
            >
              Готово
            </button>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}
